import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  MailOutline,
  LockOutlined,
  Visibility,
  VisibilityOff,
  ArrowForward,
  Check,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AuthLayout from './auth/AuthLayout';
import {
  authColors,
  fontStack,
  AuthField,
  AuthButton,
} from './auth/authStyles';

const Login = ({ onSwitchToRegister, onForgotPassword, onBackToWelcome }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { login } = useAuth();

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  React.useEffect(() => {
    const accountDeleted = localStorage.getItem('accountDeleted');
    if (accountDeleted === 'true') {
      setSuccessMessage('Votre compte a été supprimé avec succès');
      localStorage.removeItem('accountDeleted');
      setTimeout(() => setSuccessMessage(''), 5000);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      login(response.data.user, response.data.token);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      brandTitle="Donnez du sens à vos capteurs IoT."
      brandSubtitle="Climatrack, Aquacheck et Greenwatch sur une seule interface. Supervisez, analysez et anticipez en quasi temps réel."
      error={error}
      success={successMessage}
      onBack={onBackToWelcome}
    >
      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* En-tête */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Typography sx={{ color: authColors.textPrimary, fontSize: { xs: '26px', md: '30px' }, fontWeight: 700, fontFamily: fontStack }}>
            Bon retour 👋
          </Typography>
          <Typography sx={{ color: authColors.textMuted, fontSize: '14px', fontFamily: fontStack }}>
            Connectez-vous à votre espace Ecowatch 360.
          </Typography>
        </Box>

        <AuthField
          label="Adresse email"
          icon={<MailOutline />}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="vous@exemple.com"
          required
          autoComplete="email"
        />

        <AuthField
          label="Mot de passe"
          rightLabel={
            <Box
              component="button"
              type="button"
              onClick={onForgotPassword}
              sx={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                color: authColors.accentGreen,
                fontSize: '12px',
                fontWeight: 500,
                fontFamily: fontStack,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Mot de passe oublié ?
            </Box>
          }
          icon={<LockOutlined />}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••"
          required
          autoComplete="current-password"
          endIcon={showPassword ? <VisibilityOff /> : <Visibility />}
          onEndIconClick={() => setShowPassword(!showPassword)}
        />

        {/* Se souvenir de moi */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}
          onClick={() => setRememberMe(!rememberMe)}
        >
          <Box
            sx={{
              width: 18,
              height: 18,
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: rememberMe ? authColors.accentGreen : 'transparent',
              border: `1px solid ${rememberMe ? authColors.accentGreen : authColors.borderSubtle}`,
            }}
          >
            {rememberMe && <Check sx={{ fontSize: 12, color: authColors.onAccent }} />}
          </Box>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', fontFamily: fontStack }}>
            Se souvenir de moi sur ce poste
          </Typography>
        </Box>

        <AuthButton loading={loading}>
          {loading ? 'Connexion...' : 'Se connecter'}
          {!loading && <ArrowForward sx={{ fontSize: 18 }} />}
        </AuthButton>

        {/* Séparateur */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: authColors.borderSubtle }} />
          <Typography sx={{ color: authColors.textPlaceholder, fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', fontFamily: fontStack }}>
            OU
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: authColors.borderSubtle }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
          <Typography sx={{ color: authColors.textMuted, fontSize: '13px', fontFamily: fontStack }}>
            Pas encore de compte ?
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={onSwitchToRegister}
            sx={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
              color: authColors.accentGreen,
              fontSize: '13px',
              fontWeight: 600,
              fontFamily: fontStack,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Créer un compte
          </Box>
        </Box>
      </Box>
    </AuthLayout>
  );
};

export default Login;
